import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { SectionLabel } from '@/components/ui/section-label';
import { cn } from '@/lib/utils';
import { useDialogStream } from '@/hooks/use-dialog-stream';
import { useDialogSession, useGetOrCreateCoach } from '@/hooks/use-dialog';
import { useRefocusAfterStreaming } from '@/hooks/use-refocus-after-streaming';
import { useConfig } from '@/app/ConfigProvider';
import { getOrCreateClientSessionId, resetClientSessionId } from '@/lib/clientSessionId';
import { Send, Loader2, X, Sparkles, Lightbulb, Target, Users, Check, ArrowRight, RotateCcw } from 'lucide-react';
import type { DialogPublicInfo, IdeaCoachSessionState, IdeaDraft, PersistedMessage } from '@/types/dialog';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  excludeFromHistory?: boolean;
}

const WELCOME =
  "Hi! I'm here to help you sharpen your product idea before you take it through validation. " +
  "We'll land on three things: what you want to build, the specific outcome it lets your customer achieve (the aspiration), and who it's for. " +
  "To start — in a sentence or two, what's the idea you're chewing on?";

// ---------------------------------------------------------------------------
// Modal entry: owns get-or-create of the project's singleton coach dialog,
// then renders the chat once a token is in hand. Mirrors the Dialog page →
// DialogChat split so the chat's hooks always run with a real token.
// ---------------------------------------------------------------------------

interface IdeaCoachDialogProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  // Called when the PM applies the coach's capture — the parent seeds step 1's
  // form with these values and lets the PM review + save through the normal path.
  onApply: (draft: IdeaDraft) => void;
}

export function IdeaCoachDialog({ projectId, isOpen, onClose, onApply }: IdeaCoachDialogProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border rounded-lg shadow-xl w-full max-w-2xl mx-4 h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Idea coach
          </h2>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <IdeaCoachPanel projectId={projectId} onApply={onApply} />
      </div>
    </div>
  );
}

// Embeddable coach: owns get-or-create of the project's singleton coach
// dialog, then renders the chat. Used inline as step 1's front door for
// empty projects and inside IdeaCoachDialog for the modal path. Mount-gated:
// the parent renders it only when the coach should be live.
export function IdeaCoachPanel({
  projectId,
  onApply,
}: {
  projectId: string;
  onApply: (draft: IdeaDraft) => void;
}) {
  const coach = useGetOrCreateCoach();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    coach
      .mutateAsync(projectId)
      .then((d) => {
        if (!cancelled) setToken(d.token);
      })
      .catch(() => {
        /* surfaced via coach.isError below */
      });
    return () => {
      cancelled = true;
    };
    // coach is a stable mutation handle; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (coach.isError) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
        Couldn't start the coach. Refresh and try again.
      </div>
    );
  }
  if (!token) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Starting coach" />
      </div>
    );
  }
  return <IdeaCoachChat token={token} onApply={onApply} />;
}

// ---------------------------------------------------------------------------
// The chat itself. Reuses the streaming hook + chat scaffold; the JTBD
// DialogChat is too item-shaped to share, so this is a focused sibling.
// ---------------------------------------------------------------------------

interface IdeaCoachChatProps {
  token: string;
  onApply: (draft: IdeaDraft) => void;
}

function isCoachState(state: DialogPublicInfo['state']): state is IdeaCoachSessionState {
  return !!state && 'ideaDraft' in state;
}

function IdeaCoachChat({ token, onApply }: IdeaCoachChatProps) {
  // Stateful so "Start over" can reroll it: a fresh actorId resolves to an
  // empty AgentCore Memory session, restarting the conversation without
  // touching the dialog/token or the project's idea node.
  const [clientSessionId, setClientSessionId] = useState(() => getOrCreateClientSessionId(token));
  const { data: dialogInfo, isLoading } = useDialogSession(token, clientSessionId);
  const config = useConfig();
  const streamUrl = config.api.streamUrl || null;
  const stream = useDialogStream(token, streamUrl);

  const [messages, setMessages] = useState<ChatMessage[] | null>(null);
  const [draft, setDraft] = useState<IdeaDraft>({});
  const [input, setInput] = useState('');
  const [wrapUpSuggested, setWrapUpSuggested] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useRefocusAfterStreaming(inputRef, stream.isStreaming);

  // Hydrate once the session GET resolves: welcome on top, then any persisted
  // turns + the last cumulative draft. Guarded on `messages === null` so a
  // later refetch doesn't clobber an in-progress conversation.
  useEffect(() => {
    if (!dialogInfo || messages !== null) return;
    const welcome: ChatMessage = { id: 'welcome', role: 'assistant', content: WELCOME };
    if (isCoachState(dialogInfo.state)) {
      setMessages([welcome, ...hydrateMessages(dialogInfo.state.messages)]);
      setDraft(dialogInfo.state.ideaDraft ?? {});
    } else {
      setMessages([welcome]);
    }
  }, [dialogInfo, messages]);

  // Fold each completed turn into the transcript + draft.
  useEffect(() => {
    if (!stream.result) return;
    const intervened = stream.result.intervention;
    const data = stream.result.data;
    setWrapUpSuggested(!intervened && data.shouldWrapUp === true);
    setMessages((prev) => {
      const base = (prev ?? []).filter((m) => m.id !== 'streaming');
      return [...base, { id: `ai-${Date.now()}`, role: 'assistant', content: data.reply }];
    });
    // The lambda already keeps the prior draft on a blocked turn; never adopt
    // model output from one here either.
    if (!intervened && data.ideaDraft) setDraft(data.ideaDraft);
  }, [stream.result]);

  useEffect(() => {
    if (stream.error) {
      setMessages((prev) => [
        ...(prev ?? []),
        { id: `error-${Date.now()}`, role: 'assistant', content: stream.error! },
      ]);
    }
  }, [stream.error]);

  useEffect(() => {
    const behavior: ScrollBehavior = stream.isStreaming ? 'auto' : 'smooth';
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, [messages, stream.streamingText, stream.isStreaming]);

  const handleSend = () => {
    if (!input.trim() || stream.isStreaming) return;
    const userMessage = input.trim();
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setMessages((prev) => [...(prev ?? []), { id: `user-${Date.now()}`, role: 'user', content: userMessage }]);
    setWrapUpSuggested(false);
    stream.sendMessage({ message: userMessage, clientSessionId });
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value.slice(0, 2000));
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
  };

  // Discard this coach conversation and begin a fresh one. Non-destructive to
  // the project — the idea node is only written on Apply. setMessages(null)
  // lets the hydrate effect re-run against the new (empty) session.
  const handleStartOver = () => {
    if (stream.isStreaming) return;
    setClientSessionId(resetClientSessionId(token));
    setMessages(null);
    setDraft({});
    setWrapUpSuggested(false);
    setInput('');
  };

  // Enough to apply once we have any captured field — the PM reviews and edits
  // in the form afterward, so we don't gate hard on all three. A truthy
  // customerSegment already implies ≥1 non-empty subfield (normaliseIdeaTurn
  // drops empties), so we don't single out `role`.
  const canApply = !!(draft.idea || draft.aspiration || draft.customerSegment);

  if (isLoading || messages === null) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading coach" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Transcript */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((msg) => (
          <div key={msg.id} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap',
                msg.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted rounded-bl-md',
              )}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {stream.isStreaming && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-2xl rounded-bl-md px-4 py-3 text-sm bg-muted whitespace-pre-wrap">
              {stream.streamingText || '...'}
              <span className="inline-block w-1.5 h-4 bg-current ml-0.5 animate-pulse" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <CoachDraftPanel
        draft={draft}
        onApply={() => onApply(draft)}
        canApply={canApply}
        suggested={wrapUpSuggested}
        onStartOver={handleStartOver}
        startOverDisabled={stream.isStreaming}
      />

      {/* Input */}
      <div className="px-4 py-3 border-t bg-card">
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            rows={1}
            className="flex-1 px-4 py-2 border rounded-xl bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/50 resize-none overflow-y-auto"
            style={{ maxHeight: '160px' }}
            disabled={stream.isStreaming}
          />
          <Button onClick={handleSend} disabled={!input.trim() || stream.isStreaming} size="icon" className="rounded-xl shrink-0">
            {stream.isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CoachDraftPanel({
  draft,
  onApply,
  canApply,
  suggested,
  onStartOver,
  startOverDisabled,
}: {
  draft: IdeaDraft;
  onApply: () => void;
  canApply: boolean;
  suggested?: boolean;
  onStartOver: () => void;
  startOverDisabled?: boolean;
}) {
  const [confirmingReset, setConfirmingReset] = useState(false);
  const segment = draft.customerSegment
    ? [draft.customerSegment.role, draft.customerSegment.industry, draft.customerSegment.description]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <div className="border-t bg-muted/30 px-4 py-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <SectionLabel>Captured so far</SectionLabel>
        <div className="flex items-center gap-2 shrink-0">
          {confirmingReset ? (
            <>
              <span className="text-xs text-muted-foreground hidden sm:inline">Discard this conversation?</span>
              <Button
                variant="destructive"
                size="sm"
                className="h-7"
                onClick={() => {
                  setConfirmingReset(false);
                  onStartOver();
                }}
              >
                Discard &amp; restart
              </Button>
              <Button variant="ghost" size="sm" className="h-7" onClick={() => setConfirmingReset(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-7"
                onClick={() => setConfirmingReset(true)}
                disabled={startOverDisabled}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                Start over
              </Button>
              <Button
                size="sm"
                onClick={onApply}
                disabled={!canApply}
                className={cn('h-7', suggested && canApply && 'ring-2 ring-primary ring-offset-2 ring-offset-background motion-safe:animate-pulse')}
              >
                Apply to idea
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="grid gap-1.5">
        <DraftRow icon={Lightbulb} label="Idea" value={draft.idea} />
        <DraftRow icon={Target} label="Aspiration" value={draft.aspiration} />
        <DraftRow icon={Users} label="Segment" value={segment || undefined} />
      </div>
    </div>
  );
}

function DraftRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value?: string;
}) {
  const filled = !!value;
  return (
    <div className={cn('flex items-start gap-2 text-xs', filled ? 'text-foreground' : 'text-muted-foreground')}>
      {filled ? <Check className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-600" /> : <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0 opacity-50" />}
      <span className="font-medium w-20 shrink-0">{label}</span>
      <span className="min-w-0 flex-1">{value || <span className="italic">not yet</span>}</span>
    </div>
  );
}

function hydrateMessages(persisted: PersistedMessage[]): ChatMessage[] {
  return persisted.map((m, i) => ({
    id: `hydrated-${i}`,
    role: m.role,
    content: m.content,
    ...(m.intervention ? { excludeFromHistory: true } : {}),
  }));
}
